import { GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";

import { auth } from "@/firebase";

import { getToken } from "./client";

let tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

export const startTokenRefreshTimer = () => {
	tokenRefreshTimer = setInterval(async () => {
		try {
			await getToken();
		} catch (error) {
			console.error("Error refreshing token:", error);
		}
	}, TOKEN_REFRESH_INTERVAL);
};

export const getRefreshToken = async (uid: string) => {
	return new Promise<string>((resolve) => {
		try {
			setTimeout(async () => {
				const response = await fetch(
					`${import.meta.env.VITE_REFRESH_TOKEN_URL}?uid=${uid}`,
				);
				const result = await response.json();
				resolve(result?.data?.user[0]?.id);
			}, 3000);
		} catch (error) {
			console.error("Error occured while fetching refresh token", error);
			throw error;
		}
	});
};

/**
 * Initiates Google Sign-In using popup flow
 */
export const loginWithGoogle = async () => {
	try {
		const provider = new GoogleAuthProvider();
		// Force account selection for better UX
		provider.setCustomParameters({
			prompt: "select_account",
		});
		const result = await signInWithPopup(auth, provider);
		return result.user;
	} catch (error) {
		console.error("Error initiating Google Sign-In:", error);
		throw new Error("Failed to initiate Google Sign-In");
	}
};

/**
 * Validates that the user has the "driver" role in Hasura JWT claims
 * and sets up token refresh if valid
 */
export const validateUserRole = async (user: any) => {
	try {
		console.log("🔍 Validating user role for:", {
			uid: user.uid,
			email: user.email,
			displayName: user.displayName,
		});

		const tokenResult = await user.getIdTokenResult();
		console.log("🎫 Token claims:", tokenResult.claims);

		const hasuraClaims = tokenResult.claims["https://hasura.io/jwt/claims"];
		console.log("🔐 Hasura claims:", hasuraClaims);

		const allowedClaims = (
			hasuraClaims as { "x-hasura-allowed-roles": string }
		)?.["x-hasura-allowed-roles"] as string;
		console.log("👤 User role:", allowedClaims);

		if (!allowedClaims.includes("driver")) {
			console.error("❌ User role is not 'driver':", allowedClaims);
			await signOut(auth);
			throw new Error("You are not registered as a driver");
		}

		console.log("✅ Driver role verified!");
		await getRefreshToken(user.uid);
		startTokenRefreshTimer();
		console.log("✅ User validated and token refresh started!");
		return true;
	} catch (error) {
		console.error("❌ Error validating user role:", error);
		if ((error as Error).message.includes("not registered as a driver")) {
			throw error;
		}
		throw new Error("Failed to validate user role");
	}
};

export const logout = async () => {
	localStorage.clear();
	await signOut(auth);
	if (tokenRefreshTimer) {
		clearInterval(tokenRefreshTimer);
		tokenRefreshTimer = null;
	}
	window.location.replace("/login");
};
